export type GatewayCheckId =
  | 'connection'
  | 'crypto'
  | 'storage'
  | 'automation'
  | 'timing'
  | 'service'
  | 'turnstile'
  | 'work';

export type GatewayCheckState = 'pending' | 'running' | 'passed' | 'warned' | 'failed' | 'skipped';

export interface GatewayCheck {
  id: GatewayCheckId;
  label: string;
  detail: string;
  state: GatewayCheckState;
  required: boolean;
}

export const GATEWAY_MIN_DWELL_MS = 600;

export const GATEWAY_WORK_ITERATIONS = 300_000;

export const GATEWAY_SERVICE_TIMEOUT_MS = 4000;

export const GATEWAY_VERIFY_TIMEOUT_MS = 6000;

export const GATEWAY_TURNSTILE_GRACE_MS = 15_000;

const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const SITE_KEY_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

export function siteKeyOf(source: Record<string, unknown>): string | null {
  const raw = source['VITE_TURNSTILE_SITE_KEY'];
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  return SITE_KEY_PATTERN.test(trimmed) ? trimmed : null;
}

export function isSecureOrigin(): boolean {
  if (window.isSecureContext === true) {
    return true;
  }
  return LOOPBACK_HOSTS.includes(window.location.hostname);
}

export function haveWebCrypto(): boolean {
  const subtle = globalThis.crypto?.subtle;
  return typeof subtle?.deriveBits === 'function' && typeof globalThis.crypto?.getRandomValues === 'function';
}

export function haveStorage(): boolean {
  try {
    const probe = 'luacentrix.gateway.probe';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function isAutomated(): boolean {
  return navigator.webdriver === true;
}

export function browserChecks(): GatewayCheck[] {
  const secure = isSecureOrigin();
  const cryptoReady = haveWebCrypto();
  const storageReady = haveStorage();
  const automated = isAutomated();
  return [
    {
      id: 'connection',
      label: 'Secure connection',
      detail: secure ? 'Served over HTTPS.' : 'This page is not on a secure origin.',
      state: secure ? 'passed' : 'failed',
      required: true,
    },
    {
      id: 'crypto',
      label: 'Browser crypto',
      detail: cryptoReady ? 'WebCrypto key derivation is available.' : 'WebCrypto is unavailable.',
      state: cryptoReady ? 'passed' : 'failed',
      required: true,
    },
    {
      id: 'storage',
      label: 'Local storage',
      detail: storageReady ? 'Storage is writable.' : 'Storage is blocked; the pass lasts this tab only.',
      state: storageReady ? 'passed' : 'warned',
      required: false,
    },
    {
      id: 'automation',
      label: 'Automation signal',
      detail: automated
        ? 'navigator.webdriver is set, which marks this as an automated browser.'
        : 'No automation flag is set.',
      state: automated ? 'failed' : 'passed',
      required: true,
    },
  ];
}

export function initialChecks(turnstileConfigured: boolean): GatewayCheck[] {
  return [
    ...browserChecks(),
    {
      id: 'timing',
      label: 'Interaction timing',
      detail: 'Measured from page load.',
      state: 'pending',
      required: true,
    },
    {
      id: 'service',
      label: 'Service reachability',
      detail: 'Runs automatically.',
      state: 'pending',
      required: false,
    },
    {
      id: 'turnstile',
      label: 'Human verification',
      detail: turnstileConfigured
        ? 'Waiting for Cloudflare Turnstile.'
        : 'Turnstile is not configured; browser checks apply.',
      state: turnstileConfigured ? 'pending' : 'skipped',
      required: false,
    },
    {
      id: 'work',
      label: 'Key derivation',
      detail: 'Runs once the browser is verified.',
      state: 'pending',
      required: true,
    },
  ];
}

export function mergeChecks(current: GatewayCheck[], updates: GatewayCheck[]): GatewayCheck[] {
  const byId = new Map(updates.map(check => [check.id, check]));
  return current.map(check => byId.get(check.id) ?? check);
}

export function replaceCheck(
  current: GatewayCheck[],
  id: GatewayCheckId,
  state: GatewayCheckState,
  detail: string,
): GatewayCheck[] {
  return current.map(check => (check.id === id ? { ...check, state, detail } : check));
}

export function delay(millis: number): Promise<void> {
  return new Promise(resolve => {
    window.setTimeout(resolve, millis);
  });
}

export async function waitForDwell(startedAt: number, minimumMillis = GATEWAY_MIN_DWELL_MS): Promise<number> {
  const elapsed = performance.now() - startedAt;
  const remaining = minimumMillis - elapsed;
  if (remaining > 0) {
    await delay(remaining);
  }
  return Math.round(Math.max(elapsed, minimumMillis));
}

export async function runKeyDerivation(iterations = GATEWAY_WORK_ITERATIONS): Promise<number> {
  const started = performance.now();
  const subtle = globalThis.crypto.subtle;
  const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const material = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const key = await subtle.importKey('raw', material, 'PBKDF2', false, ['deriveBits']);
  await subtle.deriveBits({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, key, 256);
  return Math.round(performance.now() - started);
}

export function apiBaseUrlOf(source: Record<string, unknown>): string {
  const raw = source['VITE_API_BASE_URL'];
  const value = typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : window.location.origin;
  return value.replace(/\/+$/, '');
}

export async function probeService(baseUrl: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), GATEWAY_SERVICE_TIMEOUT_MS);
  try {
    const response = await fetch(`${baseUrl}/healthz`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}
